import * as React from 'react';
import type { Row } from '@tanstack/react-table';

import type { SurfaceToken } from '../../components/ui/box-classes';
import type { DataTableDensity } from './data-table-types';

// ---------------------------------------------------------------------------
// Surface context — the surface the table paints on
//
// A table is spread across a dozen files: the header, the pinned cells, the
// row washes, the group nav, the editable cell and the drag veil all have to
// name the SAME surface or the table cuts a differently-colored hole in whatever
// it sits on. Threading a prop through all of them would be a dozen chances to
// forget one, so the surface travels the way density already does.
//
// Default `background`: a table is a page-level view unless told otherwise.
// ---------------------------------------------------------------------------

const DataTableSurfaceContext = React.createContext<SurfaceToken>('background');

function DataTableSurfaceProvider({
  surface,
  children,
}: {
  surface: SurfaceToken;
  children: React.ReactNode;
}) {
  return (
    <DataTableSurfaceContext.Provider value={surface}>
      {children}
    </DataTableSurfaceContext.Provider>
  );
}

function useDataTableSurface() {
  return React.useContext(DataTableSurfaceContext);
}

const DataTableDensityContext = React.createContext<DataTableDensity>('regular');

function DataTableDensityProvider({
  density,
  children,
}: {
  density: DataTableDensity;
  children: React.ReactNode;
}) {
  return (
    <DataTableDensityContext.Provider value={density}>
      {children}
    </DataTableDensityContext.Provider>
  );
}

function useDataTableDensity() {
  return React.useContext(DataTableDensityContext);
}

// ---------------------------------------------------------------------------
// Group rows context — lets the selection header scope to current group
// ---------------------------------------------------------------------------

 
const DataTableGroupRowsContext = React.createContext<Row<any>[] | null>(null);

// True when the group has settled at zero entities — not loading, nothing
// fetched, and a trustworthy zero count. Distinct from "no loaded rows": a
// paginated group can hold zero fetched rows while totalCount says rows
// exist server-side, and that group still offers select-all.
const DataTableGroupKnownEmptyContext = React.createContext<boolean>(false);

function DataTableGroupRowsProvider<TData>({
  rows,
  knownEmpty = false,
  children,
}: {
  rows: Row<TData>[];
  knownEmpty?: boolean;
  children: React.ReactNode;
}) {
  return (
    <DataTableGroupRowsContext.Provider value={rows}>
      <DataTableGroupKnownEmptyContext.Provider value={knownEmpty}>
        {children}
      </DataTableGroupKnownEmptyContext.Provider>
    </DataTableGroupRowsContext.Provider>
  );
}

function useDataTableGroupRows<TData>(): Row<TData>[] | null {
  return React.useContext(DataTableGroupRowsContext);
}

function useDataTableGroupKnownEmpty(): boolean {
  return React.useContext(DataTableGroupKnownEmptyContext);
}

// ---------------------------------------------------------------------------
// Shift-select context — tracks last selected row ID for range selection
// ---------------------------------------------------------------------------

const DataTableShiftSelectContext = React.createContext<React.RefObject<string | null> | null>(null);

function DataTableShiftSelectProvider({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<string | null>(null);
  return (
    <DataTableShiftSelectContext.Provider value={ref}>
      {children}
    </DataTableShiftSelectContext.Provider>
  );
}

function useDataTableShiftSelect() {
  return React.useContext(DataTableShiftSelectContext);
}

// ---------------------------------------------------------------------------
// Header drag context — lets the sortable header swallow the press that the
// pointer-up of a column-reorder drag would otherwise turn into a sort, and
// show a grab cursor on reorderable columns.
// ---------------------------------------------------------------------------

type DataTableHeaderDragValue = {
  didDragRef: React.RefObject<boolean>;
  reorderableIds: ReadonlySet<string>;
};

const DataTableHeaderDragContext =
  React.createContext<DataTableHeaderDragValue | null>(null);

function DataTableHeaderDragProvider({
  didDragRef,
  reorderableIds,
  children,
}: DataTableHeaderDragValue & {
  children: React.ReactNode;
}) {
  const value = React.useMemo(
    () => ({ didDragRef, reorderableIds }),
    [didDragRef, reorderableIds],
  );
  return (
    <DataTableHeaderDragContext.Provider value={value}>
      {children}
    </DataTableHeaderDragContext.Provider>
  );
}

function useDataTableHeaderDrag() {
  return React.useContext(DataTableHeaderDragContext);
}

// ---------------------------------------------------------------------------
// First-header context — marks the header cell of the first content column
// (the first non-structural column of its header row). DataTableColumnHeader
// reads it to default that column's hover to the gradient wash: the leading
// column is typically the wide fill column, where the flat full-cell wash
// reads as a giant control. Context rather than a prop because consumers
// compose DataTableColumnHeader inside their own header components, where the
// table cannot hand it anything directly.
// ---------------------------------------------------------------------------

const DataTableFirstHeaderContext = React.createContext(false);

function DataTableFirstHeaderProvider({
  isFirst,
  children,
}: {
  isFirst: boolean;
  children: React.ReactNode;
}) {
  return (
    <DataTableFirstHeaderContext.Provider value={isFirst}>
      {children}
    </DataTableFirstHeaderContext.Provider>
  );
}

function useDataTableIsFirstHeader() {
  return React.useContext(DataTableFirstHeaderContext);
}

// ---------------------------------------------------------------------------
// Add-column context — feeds the actions-column header's "+" picker
//
// The actions column is a static column DEFINITION (created once in the
// columns memo), but the set of hidden columns changes with every visibility
// edit. Carrying that set through context keeps the column definitions —
// and with them every per-group TanStack table — referentially stable across
// visibility changes; only the header's picker re-renders.
// ---------------------------------------------------------------------------

type DataTableAddColumnValue = {
  /** Hidden, addable columns in table order. */
  entries: readonly { id: string; label: string }[];
  /** Make the column visible. */
  onAdd: (columnId: string) => void;
};

const DataTableAddColumnContext =
  React.createContext<DataTableAddColumnValue | null>(null);

function DataTableAddColumnProvider({
  value,
  children,
}: {
  value: DataTableAddColumnValue | null;
  children: React.ReactNode;
}) {
  return (
    <DataTableAddColumnContext.Provider value={value}>
      {children}
    </DataTableAddColumnContext.Provider>
  );
}

function useDataTableAddColumn() {
  return React.useContext(DataTableAddColumnContext);
}

// ---------------------------------------------------------------------------
// Select-all context — lets header/cell read and toggle group select-all state
// ---------------------------------------------------------------------------

type DataTableSelectAllContextValue = {
  isSelectAll: boolean;
  setSelectAll: (value: boolean) => void;
};

const DataTableSelectAllContext = React.createContext<DataTableSelectAllContextValue>({
  isSelectAll: false,
  setSelectAll: () => {},
});

function DataTableSelectAllProvider({
  isSelectAll,
  setSelectAll,
  children,
}: DataTableSelectAllContextValue & { children: React.ReactNode }) {
  const value = React.useMemo(
    () => ({ isSelectAll, setSelectAll }),
    [isSelectAll, setSelectAll],
  );
  return (
    <DataTableSelectAllContext.Provider value={value}>
      {children}
    </DataTableSelectAllContext.Provider>
  );
}

function useDataTableSelectAll() {
  return React.useContext(DataTableSelectAllContext);
}

// ---------------------------------------------------------------------------
// Shared-header select-all — a grouped table with `sharedHeader` renders ONE
// column header (hoisted above the first group), so its checkbox must address
// EVERY group. The per-group table it happens to mount inside can only reach
// group 0's rows; the outer DataTable owns all group selections and provides
// the cross-group state + toggle here. Absent (null) everywhere else — the
// header checkbox then keeps its group-scoped behavior.
// ---------------------------------------------------------------------------

type DataTableSharedSelectAllValue = {
  isAllSelected: boolean;
  isSomeSelected: boolean;
  toggle: (value: boolean) => void;
};

const DataTableSharedSelectAllContext =
  React.createContext<DataTableSharedSelectAllValue | null>(null);

function DataTableSharedSelectAllProvider({
  value,
  children,
}: {
  value: DataTableSharedSelectAllValue | null;
  children: React.ReactNode;
}) {
  return (
    <DataTableSharedSelectAllContext.Provider value={value}>
      {children}
    </DataTableSharedSelectAllContext.Provider>
  );
}

function useDataTableSharedSelectAll() {
  return React.useContext(DataTableSharedSelectAllContext);
}

// ---------------------------------------------------------------------------
// Footer chrome: what a group footer ("+ Add …") needs to align with the
// content line (paddingX / selection column + the first cell's own left pad).
// ---------------------------------------------------------------------------

type DataTableFooterChrome = {
  /** px from the table edge to the first column's content line. */
  contentLeadingPx: number;
};

const DataTableFooterChromeContext =
  React.createContext<DataTableFooterChrome>({ contentLeadingPx: 0 });

function DataTableFooterChromeProvider({
  value,
  children,
}: {
  value: DataTableFooterChrome;
  children: React.ReactNode;
}) {
  return (
    <DataTableFooterChromeContext.Provider value={value}>
      {children}
    </DataTableFooterChromeContext.Provider>
  );
}

function useDataTableFooterChrome() {
  return React.useContext(DataTableFooterChromeContext);
}

// ---------------------------------------------------------------------------
// Row kebab context — the row's "…" menu, offered to its host cell
//
// DataTable hosts the row's kebab in the row's leading content cell. The cell
// may render its own hover-revealed action cluster (DataTableHoverActionsCell),
// and two floating clusters on one edge cannot share a fade: each ramps its
// own 16px lead-in, and the kebab's ramp lands on the cell cluster's last
// button. So the host OFFERS the kebab through this context and the cell's
// cluster ADOPTS it as its last member — one cluster, one ramp leading every
// action. A cell with no hover cluster adopts nothing, and the host renders
// the kebab as a standalone float instead.
//
// Adoption is announced from a layout effect (`adopt` returns the release),
// so the host's standalone kebab is gone before the first paint.
// ---------------------------------------------------------------------------

type DataTableRowKebabValue = {
  /** The row's `renderRowActions` result — the kebab menu's items. */
  actions: React.ReactNode;
  /** Claim the kebab for the caller's cluster. Returns the release. */
  adopt: () => () => void;
};

const DataTableRowKebabContext =
  React.createContext<DataTableRowKebabValue | null>(null);

function DataTableRowKebabProvider({
  value,
  children,
}: {
  value: DataTableRowKebabValue;
  children: React.ReactNode;
}) {
  return (
    <DataTableRowKebabContext.Provider value={value}>
      {children}
    </DataTableRowKebabContext.Provider>
  );
}

function useDataTableRowKebab() {
  return React.useContext(DataTableRowKebabContext);
}

export {
  DataTableAddColumnProvider,
  useDataTableAddColumn,
  DataTableSurfaceProvider,
  useDataTableSurface,
  DataTableDensityProvider,
  useDataTableDensity,
  DataTableFooterChromeProvider,
  useDataTableFooterChrome,
  DataTableGroupRowsProvider,
  useDataTableGroupRows,
  useDataTableGroupKnownEmpty,
  DataTableShiftSelectProvider,
  useDataTableShiftSelect,
  DataTableSelectAllProvider,
  useDataTableSelectAll,
  DataTableSharedSelectAllProvider,
  useDataTableSharedSelectAll,
  DataTableHeaderDragProvider,
  useDataTableHeaderDrag,
  DataTableFirstHeaderProvider,
  useDataTableIsFirstHeader,
  DataTableRowKebabProvider,
  useDataTableRowKebab,
};
export type { DataTableSharedSelectAllValue, DataTableRowKebabValue };
